/**
 * Geometry for the 3-D view, built on the CPU from the full-resolution grids.
 *
 * THE PLAN. One mesh vertex per solver cell, taken every `stride` cells so the
 * drawn mesh is at most MAX_VERTS x MAX_VERTS. Each vertex sits at the true centre
 * of the cell it samples, and keeps that cell's full-resolution index, so nothing
 * the page reports is ever read from the decimated mesh.
 *
 * NORMALS. Per vertex, from central differences of the height field along the
 * grid's two axes, solved into east/north slopes (m/m). The vertex shader
 * multiplies those slopes by the vertical exaggeration, so the normal is the
 * normal of the surface as drawn, and a new exaggeration re-lights the mesh
 * without rebuilding it.
 *
 * DEM VOIDS are filled FOR DRAWING ONLY, from the mean of their valid neighbours,
 * so the mesh has no pits; the hover still reports the cell as a void.
 */

import { WATER_3D, WATER_3D_RAMP, rampRgba } from '../../components/Legend'
import { cellLonLat, type Frame, type SceneMeta, type Terrain } from './data'

export const MAX_VERTS = 512

export interface Plan {
  width: number
  height: number
  stride: number
  nr: number
  nc: number
  /** full-resolution cell index of each vertex */
  cell: Int32Array
  lon: Float64Array
  lat: Float64Array
  /** local east/north metres, for slopes only */
  east: Float64Array
  north: Float64Array
}

export function makePlan(meta: SceneMeta, maxVerts = MAX_VERTS): Plan {
  const { width, height, lattice } = meta
  const stride = Math.max(1, Math.ceil(Math.max(width, height) / maxVerts))
  const nr = Math.floor((height - 1) / stride) + 1
  const nc = Math.floor((width - 1) / stride) + 1
  const nv = nr * nc
  const cell = new Int32Array(nv)
  const lon = new Float64Array(nv)
  const lat = new Float64Array(nv)
  const east = new Float64Array(nv)
  const north = new Float64Array(nv)
  const [lon0, lat0] = cellLonLat(lattice, height / 2, width / 2)
  const kx = 111320 * Math.cos((lat0 * Math.PI) / 180)
  const ky = 110574
  for (let i = 0; i < nr; i += 1) {
    for (let j = 0; j < nc; j += 1) {
      const v = i * nc + j
      const row = i * stride
      const col = j * stride
      cell[v] = row * width + col
      const [x, y] = cellLonLat(lattice, row, col)
      lon[v] = x
      lat[v] = y
      east[v] = (x - lon0) * kx
      north[v] = (y - lat0) * ky
    }
  }
  return { width, height, stride, nr, nc, cell, lon, lat, east, north }
}

/** East and north slope (m/m) of a vertex height field, by central differences on the grid axes. */
export function slopes(plan: Plan, z: Float32Array | Float64Array, out: Float32Array, offset = 0): void {
  const { nr, nc, east, north } = plan
  for (let i = 0; i < nr; i += 1) {
    const i0 = Math.max(0, i - 1)
    const i1 = Math.min(nr - 1, i + 1)
    for (let j = 0; j < nc; j += 1) {
      const j0 = Math.max(0, j - 1)
      const j1 = Math.min(nc - 1, j + 1)
      const v = i * nc + j
      // along columns (j) and along rows (i)
      const ae = east[i * nc + j1] - east[i * nc + j0]
      const an = north[i * nc + j1] - north[i * nc + j0]
      const az = z[i * nc + j1] - z[i * nc + j0]
      const be = east[i1 * nc + j] - east[i0 * nc + j]
      const bn = north[i1 * nc + j] - north[i0 * nc + j]
      const bz = z[i1 * nc + j] - z[i0 * nc + j]
      const det = ae * bn - an * be
      out[offset + v * 2] = det ? (az * bn - an * bz) / det : 0
      out[offset + v * 2 + 1] = det ? (ae * bz - az * be) / det : 0
    }
  }
}

// ---------------------------------------------------------------- terrain

/** Terrain fallback tint: greys into browns over the 2nd-98th percentile. Red >= green > blue at every stop. */
export const TERRAIN_STOPS: [number, [number, number, number]][] = [
  [0, [72, 70, 67]],
  [0.25, [99, 91, 81]],
  [0.5, [124, 111, 95]],
  [0.75, [151, 134, 113]],
  [1, [181, 168, 152]],
]

export function terrainTint(t: number): [number, number, number] {
  const s = TERRAIN_STOPS
  if (!(t > 0)) return s[0][1]
  for (let i = 0; i < s.length - 1; i += 1) {
    const [v0, c0] = s[i]
    const [v1, c1] = s[i + 1]
    if (t <= v1) {
      const u = (t - v0) / (v1 - v0)
      return [c0[0] + (c1[0] - c0[0]) * u, c0[1] + (c1[1] - c0[1]) * u, c0[2] + (c1[2] - c0[2]) * u]
    }
  }
  return s[s.length - 1][1]
}

/**
 * Cast shadow per vertex: march from the vertex toward the sun over the height
 * field AS DRAWN (heights x exaggeration) and record how far the terrain rises
 * above the ray. 0 = in sunlight, 1 = fully shadowed; a soft edge over `soft` metres.
 * `sun` is the light's travel direction [east, north, up] (up < 0 in daytime).
 */
export function castShadows(plan: Plan, z: Float32Array, exaggeration: number, sun: number[], soft = 30): Float32Array {
  const { nr, nc, east, north } = plan
  const out = new Float32Array(nr * nc)
  const ue = -sun[0]
  const un = -sun[1]
  const uz = -sun[2]
  const horiz = Math.hypot(ue, un)
  if (uz <= 0 || horiz === 0 || nr < 3 || nc < 3) return out
  const tanAlt = uz / horiz
  // grid axes in metres per vertex step, measured at the centre
  const c = Math.floor(nr / 2) * nc + Math.floor(nc / 2)
  const ce = east[c + 1] - east[c]
  const cn = north[c + 1] - north[c]
  const re = east[c + nc] - east[c]
  const rn = north[c + nc] - north[c]
  const det = ce * rn - cn * re
  if (!det) return out
  // one metre toward the sun, in column (dj) and row (di) steps
  const he = ue / horiz
  const hn = un / horiz
  const dj = (he * rn - hn * re) / det
  const di = (ce * hn - cn * he) / det
  const spacing = Math.min(Math.hypot(ce, cn), Math.hypot(re, rn))
  const step = spacing * 0.75
  let zmax = -Infinity
  for (let v = 0; v < z.length; v += 1) if (z[v] > zmax) zmax = z[v]
  const at = (i: number, j: number) => {
    const i0 = Math.floor(i)
    const j0 = Math.floor(j)
    const ti = i - i0
    const tj = j - j0
    const a = z[i0 * nc + j0]
    const b = z[i0 * nc + j0 + 1]
    const cc = z[(i0 + 1) * nc + j0]
    const d = z[(i0 + 1) * nc + j0 + 1]
    return ((a * (1 - tj) + b * tj) * (1 - ti) + (cc * (1 - tj) + d * tj) * ti) * exaggeration
  }
  for (let i = 0; i < nr; i += 1) {
    for (let j = 0; j < nc; j += 1) {
      const v = i * nc + j
      const z0 = z[v] * exaggeration
      const reach = (zmax * exaggeration - z0) / tanAlt
      let worst = 0
      for (let s = step; s <= reach; s += step) {
        const ii = i + di * s
        const jj = j + dj * s
        if (ii < 0 || jj < 0 || ii >= nr - 1 || jj >= nc - 1) break
        const rise = at(ii, jj) - (z0 + s * tanAlt)
        if (rise > worst) {
          worst = rise
          if (worst >= soft) break
        }
      }
      out[v] = Math.min(1, worst / soft)
    }
  }
  return out
}

export interface TerrainMesh {
  vertexCount: number
  positions: Float64Array
  gradients: Float32Array
  texCoords: Float32Array
  colors: Float32Array
  texWeight: Float32Array
  /** cast shadow, 0 lit to 1 shadowed (castShadows) */
  shade: Float32Array
  indices: Uint32Array
  /** vertex heights as drawn (voids filled), m */
  z: Float32Array
  /** the floor the side walls and the ground plane sit on, m */
  floor: number
}

export function buildTerrain(plan: Plan, terrain: Terrain, texCoord: ((lon: number, lat: number) => [number, number]) | null): TerrainMesh {
  const { nr, nc } = plan
  const nv = nr * nc
  const z = new Float32Array(nv)
  const known = new Uint8Array(nv)
  for (let v = 0; v < nv; v += 1) {
    const e = terrain.elevation[plan.cell[v]]
    if (Number.isFinite(e)) {
      z[v] = e
      known[v] = 1
    }
  }
  // Fill voids for drawing from valid neighbours, pass by pass.
  let missing = nv - known.reduce((a, b) => a + b, 0)
  for (let pass = 0; missing > 0 && pass < 200; pass += 1) {
    const add: [number, number][] = []
    for (let i = 0; i < nr; i += 1) {
      for (let j = 0; j < nc; j += 1) {
        const v = i * nc + j
        if (known[v]) continue
        let s = 0
        let n = 0
        for (let di = -1; di <= 1; di += 1) {
          for (let dj = -1; dj <= 1; dj += 1) {
            const ii = i + di
            const jj = j + dj
            if (ii < 0 || jj < 0 || ii >= nr || jj >= nc) continue
            const w = ii * nc + jj
            if (known[w]) {
              s += z[w]
              n += 1
            }
          }
        }
        if (n) add.push([v, s / n])
      }
    }
    if (!add.length) break
    for (const [v, val] of add) {
      z[v] = val
      known[v] = 1
    }
    missing -= add.length
  }
  for (let v = 0; v < nv; v += 1) if (!known[v]) z[v] = terrain.min

  const floor = terrain.min - Math.max(30, (terrain.max - terrain.min) * 0.04)
  // Perimeter, clockwise, for the side walls.
  const edge: number[] = []
  for (let j = 0; j < nc; j += 1) edge.push(j)
  for (let i = 1; i < nr; i += 1) edge.push(i * nc + nc - 1)
  for (let j = nc - 2; j >= 0; j -= 1) edge.push((nr - 1) * nc + j)
  for (let i = nr - 2; i > 0; i -= 1) edge.push(i * nc)
  const total = nv + edge.length * 2
  const positions = new Float64Array(total * 3)
  const gradients = new Float32Array(total * 2)
  const texCoords = new Float32Array(total * 2)
  const colors = new Float32Array(total * 4)
  const texWeight = new Float32Array(total)
  const shade = new Float32Array(total)
  const span = Math.max(1, terrain.p98 - terrain.p2)
  slopes(plan, z, gradients)
  for (let v = 0; v < nv; v += 1) {
    positions[v * 3] = plan.lon[v]
    positions[v * 3 + 1] = plan.lat[v]
    positions[v * 3 + 2] = z[v]
    const [r, g, b] = terrainTint((z[v] - terrain.p2) / span)
    colors.set([r / 255, g / 255, b / 255, 1], v * 4)
    if (texCoord) {
      const [u, w] = texCoord(plan.lon[v], plan.lat[v])
      texCoords[v * 2] = u
      texCoords[v * 2 + 1] = w
      texWeight[v] = 1
    }
  }
  const cx = plan.lon[Math.floor(nr / 2) * nc + Math.floor(nc / 2)]
  const cy = plan.lat[Math.floor(nr / 2) * nc + Math.floor(nc / 2)]
  edge.forEach((v, k) => {
    for (const bottom of [0, 1]) {
      const w = nv + k * 2 + bottom
      positions[w * 3] = plan.lon[v]
      positions[w * 3 + 1] = plan.lat[v]
      positions[w * 3 + 2] = bottom ? floor : z[v]
      // A near-infinite slope away from the centre: a horizontal outward normal.
      const dx = plan.lon[v] - cx
      const dy = plan.lat[v] - cy
      const len = Math.hypot(dx, dy) || 1
      gradients[w * 2] = (-dx / len) * 1e5
      gradients[w * 2 + 1] = (-dy / len) * 1e5
      colors.set([0.33, 0.3, 0.26, 1], w * 4)
      texWeight[w] = 0
    }
  })
  const tris: number[] = []
  for (let i = 0; i < nr - 1; i += 1) {
    for (let j = 0; j < nc - 1; j += 1) {
      const a = i * nc + j
      tris.push(a, a + nc, a + 1, a + 1, a + nc, a + nc + 1)
    }
  }
  for (let k = 0; k < edge.length; k += 1) {
    const top = nv + k * 2
    const next = nv + ((k + 1) % edge.length) * 2
    tris.push(top, top + 1, next, next, top + 1, next + 1)
  }
  const indices = Uint32Array.from(tris)
  return { vertexCount: total, positions, gradients, texCoords, colors, texWeight, shade, indices, z, floor }
}

/** A flat ground plane at the floor, extending the grid's bounds by `scale` each way, so there is a horizon. */
export function buildGroundPlane(bounds: [number, number, number, number], floor: number, scale = 3): TerrainMesh {
  const [w, s, e, n] = bounds
  const dx = (e - w) * scale
  const dy = (n - s) * scale
  const corners = [
    [w - dx, s - dy],
    [e + dx, s - dy],
    [e + dx, n + dy],
    [w - dx, n + dy],
  ]
  const positions = new Float64Array(12)
  corners.forEach(([x, y], k) => positions.set([x, y, floor], k * 3))
  const colors = new Float32Array(16)
  for (let k = 0; k < 4; k += 1) colors.set([0.2, 0.185, 0.165, 1], k * 4)
  return {
    vertexCount: 4,
    positions,
    gradients: new Float32Array(8),
    texCoords: new Float32Array(8),
    colors,
    texWeight: new Float32Array(4),
    shade: new Float32Array(4),
    indices: Uint32Array.from([0, 1, 2, 0, 2, 3]),
    z: new Float32Array([floor, floor, floor, floor]),
    floor,
  }
}

// ---------------------------------------------------------------- water

export interface WaterMesh {
  vertexCount: number
  positions: Float64Array
  depths: Float32Array
  grads: Float32Array
  colorA: Float32Array
  colorB: Float32Array
  flows: Float32Array
  role: Float32Array
  shade: Float32Array
  indices: Uint32Array
  wetVertices: number
}

function depthColour(d: number, out: Float32Array, at: number) {
  const [r, g, b] = rampRgba(WATER_3D_RAMP, Math.max(d, 0.05), WATER_3D.aboveTop)
  out[at] = r / 255
  out[at + 1] = g / 255
  out[at + 2] = b / 255
  out[at + 3] = 1
}

/**
 * The flood between two stored frames A and B. The vertex shader mixes A and B,
 * so the surface morphs between frames without this being rebuilt.
 *
 * Surface vertices: every mesh vertex in a quad that is wet (depth >= threshold)
 * in A or B. A dry vertex is drawn just under the ground, so the water plane
 * meets the terrain in a crisp shoreline wherever the two cross.
 *
 * Skirt: along every edge of the wet region, a wall from the water surface down
 * to the ground, so the flood edge has visible thickness.
 */
export function buildWater(
  plan: Plan,
  tz: Float32Array,
  a: Frame,
  b: Frame,
  threshold: number,
  shadeField: Float32Array | null,
): WaterMesh {
  const { nr, nc, cell } = plan
  const nv = nr * nc
  const dA = new Float32Array(nv)
  const dB = new Float32Array(nv)
  const wet = new Uint8Array(nv)
  let wetVertices = 0
  for (let v = 0; v < nv; v += 1) {
    dA[v] = a.depth[cell[v]] || 0
    dB[v] = b.depth[cell[v]] || 0
    if (dA[v] >= threshold || dB[v] >= threshold) {
      wet[v] = 1
      wetVertices += 1
    }
  }
  // Water-surface slopes for A and B (the ground where dry).
  const sA = new Float32Array(nv)
  const sB = new Float32Array(nv)
  for (let v = 0; v < nv; v += 1) {
    sA[v] = tz[v] + (dA[v] >= threshold ? dA[v] : 0)
    sB[v] = tz[v] + (dB[v] >= threshold ? dB[v] : 0)
  }
  const gA = new Float32Array(nv * 2)
  const gB = new Float32Array(nv * 2)
  slopes(plan, sA, gA)
  slopes(plan, sB, gB)

  // Quads touching the wet region, and the vertices they use.
  const quadWet = (i: number, j: number) =>
    i >= 0 && j >= 0 && i < nr - 1 && j < nc - 1 &&
    wet[i * nc + j] + wet[i * nc + j + 1] + wet[(i + 1) * nc + j] + wet[(i + 1) * nc + j + 1] > 0
  const quadFull = (i: number, j: number) =>
    i >= 0 && j >= 0 && i < nr - 1 && j < nc - 1 &&
    wet[i * nc + j] + wet[i * nc + j + 1] + wet[(i + 1) * nc + j] + wet[(i + 1) * nc + j + 1] === 4

  const surf = new Int32Array(nv).fill(-1)
  const tris: number[] = []
  let count = 0
  const use = (v: number) => {
    if (surf[v] < 0) surf[v] = count++
    return surf[v]
  }
  for (let i = 0; i < nr - 1; i += 1) {
    for (let j = 0; j < nc - 1; j += 1) {
      if (!quadWet(i, j)) continue
      const p = i * nc + j
      const q00 = use(p)
      const q01 = use(p + 1)
      const q10 = use(p + nc)
      const q11 = use(p + nc + 1)
      tris.push(q00, q10, q01, q01, q10, q11)
    }
  }
  // Skirt edges: both ends wet, and one of the two quads sharing the edge not fully wet.
  const edges: [number, number, number, number][] = [] // v0, v1, outward east, outward north
  for (let i = 0; i < nr; i += 1) {
    for (let j = 0; j < nc - 1; j += 1) {
      const v0 = i * nc + j
      if (!wet[v0] || !wet[v0 + 1]) continue
      const above = quadFull(i - 1, j)
      const below = quadFull(i, j)
      if (above && below) continue
      // row index runs south: "above" is the quad to the north
      edges.push([v0, v0 + 1, 0, above ? -1 : 1])
    }
  }
  for (let i = 0; i < nr - 1; i += 1) {
    for (let j = 0; j < nc; j += 1) {
      const v0 = i * nc + j
      if (!wet[v0] || !wet[v0 + nc]) continue
      const left = quadFull(i, j - 1)
      const right = quadFull(i, j)
      if (left && right) continue
      edges.push([v0, v0 + nc, left ? 1 : -1, 0])
    }
  }
  const total = count + edges.length * 4
  const positions = new Float64Array(total * 3)
  const depths = new Float32Array(total * 2)
  const grads = new Float32Array(total * 4)
  const colorA = new Float32Array(total * 4)
  const colorB = new Float32Array(total * 4)
  const flows = new Float32Array(total * 4)
  const role = new Float32Array(total)
  const shade = new Float32Array(total)
  const put = (w: number, v: number) => {
    shade[w] = shadeField ? shadeField[v] : 0
    positions[w * 3] = plan.lon[v]
    positions[w * 3 + 1] = plan.lat[v]
    positions[w * 3 + 2] = tz[v]
    depths[w * 2] = dA[v]
    depths[w * 2 + 1] = dB[v]
    depthColour(dA[v], colorA, w * 4)
    depthColour(dB[v], colorB, w * 4)
    if (a.u && a.v && b.u && b.v) {
      flows[w * 4] = a.u[cell[v]]
      flows[w * 4 + 1] = a.v[cell[v]]
      flows[w * 4 + 2] = b.u[cell[v]]
      flows[w * 4 + 3] = b.v[cell[v]]
    }
  }
  for (let v = 0; v < nv; v += 1) {
    const w = surf[v]
    if (w < 0) continue
    put(w, v)
    grads[w * 4] = gA[v * 2]
    grads[w * 4 + 1] = gA[v * 2 + 1]
    grads[w * 4 + 2] = gB[v * 2]
    grads[w * 4 + 3] = gB[v * 2 + 1]
  }
  edges.forEach(([v0, v1, oe, on], k) => {
    const base = count + k * 4
    ;[v0, v1].forEach((v, s) => {
      for (const bottom of [0, 1]) {
        const w = base + s * 2 + bottom
        put(w, v)
        role[w] = bottom ? 2 : 1
        // horizontal outward normal, as a near-infinite slope
        grads[w * 4] = -oe * 1e5
        grads[w * 4 + 1] = -on * 1e5
        grads[w * 4 + 2] = -oe * 1e5
        grads[w * 4 + 3] = -on * 1e5
      }
    })
    tris.push(base, base + 1, base + 2, base + 2, base + 1, base + 3)
  })
  return {
    vertexCount: total,
    positions,
    depths,
    grads,
    colorA,
    colorB,
    flows,
    role,
    shade,
    indices: Uint32Array.from(tris),
    wetVertices,
  }
}

/** The terrain mesh with a cast-shadow field (one value per plan vertex); side walls stay unshaded. */
export function withShade(mesh: TerrainMesh, plan: Plan, field: Float32Array): TerrainMesh {
  const shade = new Float32Array(mesh.vertexCount)
  shade.set(field.subarray(0, plan.nr * plan.nc))
  return { ...mesh, shade }
}
